import { useState } from 'react'
import { api, useAbruf, type ApiFehler, type Seite } from '../api'
import { Angabe, Fehleranzeige, Karte, Merkmal, Tabelle, Zustand } from '../bausteine'
import { datum, geld, zahl } from '../formate'

interface Produktzeile {
  id: string
  produktnummer: string
  bezeichnung: string
}

interface Angebotsblatt {
  id: string
  angebotsnummer: string
  status: string
  interessentName: string
  postleitzahl: string
  ort: string | null
  jahresverbrauchKwh: string
  grundpreisBrutto: string | null
  arbeitspreisCtBrutto: string | null
  jahresbetragBrutto: string | null
  gewuenschterLieferbeginn: string | null
  bindungsfristBis: string
  abschlusskanal: string
  vertragId: string | null
}

interface Abrechnungsposten {
  vertriebspartnerId: string
  name: string
  anzahl: number
  summe: string
  provisionIds: string[]
}

export default function Vertrieb() {
  const produkte = useAbruf<Produktzeile[]>('/api/produkte?kundenart=PRIVATKUNDE')
  const provisionen = useAbruf<Abrechnungsposten[]>('/api/vertrieb/provisionen/abrechenbar')
  const liste = useAbruf<Seite<Angebotsblatt>>('/api/vertrieb/angebote?groesse=15')

  const [name, setzeName] = useState('')
  const [plz, setzePlz] = useState('')
  const [ort, setzeOrt] = useState('')
  const [produktId, setzeProduktId] = useState('')
  const [verbrauch, setzeVerbrauch] = useState('3500')
  const [kanal, setzeKanal] = useState('FERNABSATZ')
  const [beginn, setzeBeginn] = useState(
    new Date(Date.now() + 30 * 86_400_000).toISOString().slice(0, 10),
  )

  const [angebot, setzeAngebot] = useState<Angebotsblatt>()
  const [fehler, setzeFehler] = useState<ApiFehler>()
  const [laeuft, setzeLaeuft] = useState(false)

  const [nummer, setzeNummer] = useState('')
  const [gesucht, setzeGesucht] = useState<Angebotsblatt>()
  const [suchfehler, setzeSuchfehler] = useState<ApiFehler>()

  async function angebotErstellen() {
    setzeFehler(undefined)
    setzeLaeuft(true)
    try {
      const neu = await api.sende<Angebotsblatt>('/api/vertrieb/angebote', {
          interessentName: name,
          kundenart: 'PRIVATKUNDE',
          email: null,
          postleitzahl: plz,
          ort,
          produktId,
          netzgebietId: null,
          jahresverbrauchKwh: Number(verbrauch),
          konzessionsabgabe: 'TARIFKUNDE_ALLGEMEIN',
          gemeindeklasse: 'BIS_25000',
          gewuenschterLieferbeginn: beginn,
          abschlusskanal: kanal,
          vertriebspartnerId: null,
      })
      setzeAngebot(neu)
      liste.erneut()
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler)
    } finally {
      setzeLaeuft(false)
    }
  }

  async function angebotSuchen() {
    setzeSuchfehler(undefined)
    setzeGesucht(undefined)
    try {
      setzeGesucht(await api.lies<Angebotsblatt>(`/api/vertrieb/angebote/${nummer}`))
    } catch (ursache) {
      setzeSuchfehler(ursache as ApiFehler)
    }
  }

  return (
    <>
      <h1>Vertrieb</h1>
      <p className="untertitel">
        An den genannten Konditionen muss sich der Lieferant nach Paragraf 41 EnWG
        festhalten lassen; das Angebot haelt sie deshalb fest.
      </p>

      <Karte titel="Angebot rechnen">
        {fehler && <Fehleranzeige fehler={fehler} />}
        <div className="leiste">
          <div className="feld">
            <label htmlFor="name">Interessent</label>
            <input
              id="name"
              style={{ width: 200 }}
              value={name}
              onChange={(ereignis) => setzeName(ereignis.target.value)}
            />
          </div>
          <div className="feld">
            <label htmlFor="plz">Postleitzahl</label>
            <input
              id="plz"
              style={{ width: 90 }}
              value={plz}
              onChange={(ereignis) => setzePlz(ereignis.target.value)}
            />
          </div>
          <div className="feld">
            <label htmlFor="ort">Ort</label>
            <input
              id="ort"
              style={{ width: 160 }}
              value={ort}
              onChange={(ereignis) => setzeOrt(ereignis.target.value)}
            />
          </div>
          <div className="feld">
            <label htmlFor="produkt">Produkt</label>
            <select
              id="produkt"
              value={produktId}
              onChange={(ereignis) => setzeProduktId(ereignis.target.value)}
            >
              <option value="">Bitte wählen</option>
              {(produkte.daten ?? []).map((produkt) => (
                <option key={produkt.id} value={produkt.id}>
                  {produkt.bezeichnung}
                </option>
              ))}
            </select>
          </div>
          <div className="feld">
            <label htmlFor="verbrauch">Jahresverbrauch (kWh)</label>
            <input
              id="verbrauch"
              type="number"
              style={{ width: 130 }}
              value={verbrauch}
              onChange={(ereignis) => setzeVerbrauch(ereignis.target.value)}
            />
          </div>
          <div className="feld">
            <label htmlFor="kanal">Abschlusskanal</label>
            <select
              id="kanal"
              value={kanal}
              onChange={(ereignis) => setzeKanal(ereignis.target.value)}
            >
              <option value="FERNABSATZ">Fernabsatz</option>
              <option value="AUSSERHALB_VON_GESCHAEFTSRAEUMEN">Außerhalb Geschäftsraum</option>
              <option value="IM_GESCHAEFTSRAUM">Im Geschäftsraum</option>
            </select>
          </div>
          <div className="feld">
            <label htmlFor="beginn">Lieferbeginn</label>
            <input
              id="beginn"
              type="date"
              value={beginn}
              onChange={(ereignis) => setzeBeginn(ereignis.target.value)}
            />
          </div>
          <button
            className="betont"
            disabled={!name || !plz || !produktId || laeuft}
            onClick={angebotErstellen}
          >
            {laeuft ? 'Rechnet …' : 'Angebot rechnen'}
          </button>
        </div>

        {angebot && <Angebotsanzeige angebot={angebot} />}
      </Karte>

      <Karte titel="Angebote">
        <Zustand
          laeuft={liste.laeuft}
          fehler={liste.fehler}
          leer={(liste.daten?.inhalt ?? []).length === 0}
          leertext="Noch kein Angebot erstellt."
        >
          <Tabelle
            spalten={[
              { name: 'Angebotsnummer' },
              { name: 'Interessent' },
              { name: 'Ort' },
              { name: 'Status' },
              { name: 'Verbrauch', zahl: true },
              { name: 'Jahresbetrag', zahl: true },
              { name: 'Bindungsfrist' },
            ]}
          >
            {(liste.daten?.inhalt ?? []).map((eintrag) => (
              <tr key={eintrag.id}>
                <td>{eintrag.angebotsnummer}</td>
                <td>{eintrag.interessentName}</td>
                <td>
                  {eintrag.postleitzahl} {eintrag.ort ?? ''}
                </td>
                <td>
                  <Statusmerkmal status={eintrag.status} />
                </td>
                <td className="zahl">{zahl(eintrag.jahresverbrauchKwh)} kWh</td>
                <td className="zahl">{geld(eintrag.jahresbetragBrutto)}</td>
                <td>{datum(eintrag.bindungsfristBis)}</td>
              </tr>
            ))}
          </Tabelle>
        </Zustand>
      </Karte>

      <Karte titel="Angebot nachschlagen">
        {suchfehler && <Fehleranzeige fehler={suchfehler} />}
        <div className="leiste" style={{ marginBottom: gesucht ? 16 : 0 }}>
          <div className="feld">
            <label htmlFor="nummer">Angebotsnummer</label>
            <input
              id="nummer"
              style={{ width: 200 }}
              placeholder="AN-2026-000001"
              value={nummer}
              onChange={(ereignis) => setzeNummer(ereignis.target.value)}
            />
          </div>
          <button disabled={!nummer} onClick={angebotSuchen}>
            Suchen
          </button>
        </div>
        {gesucht && <Angebotsanzeige angebot={gesucht} />}
      </Karte>

      <Karte titel="Abrechenbare Provisionen">
        <Zustand
          laeuft={provisionen.laeuft}
          fehler={provisionen.fehler}
          leer={(provisionen.daten ?? []).length === 0}
          leertext="Keine Provision hat die Stornohaftung überstanden."
        >
          <Tabelle
            spalten={[
              { name: 'Vertriebspartner' },
              { name: 'Verträge', zahl: true },
              { name: 'Summe', zahl: true },
            ]}
          >
            {(provisionen.daten ?? []).map((posten) => (
              <tr key={posten.vertriebspartnerId}>
                <td>{posten.name}</td>
                <td className="zahl">{posten.anzahl}</td>
                <td className="zahl">{geld(posten.summe)}</td>
              </tr>
            ))}
          </Tabelle>
        </Zustand>
      </Karte>
    </>
  )
}

/** Der Zustand eines Angebots als farbiges Merkmal. */
function Statusmerkmal({ status }: { status: string }) {
  const art =
    status === 'ANGENOMMEN'
      ? 'gut'
      : status === 'ABGELEHNT' || status === 'ZURUECKGEZOGEN'
        ? 'fehler'
        : status === 'ABGELAUFEN'
          ? 'warnung'
          : 'betont'
  return <Merkmal art={art}>{status}</Merkmal>
}

function Angebotsanzeige({ angebot }: { angebot: Angebotsblatt }) {
  return (
    <div className="angaben" style={{ marginTop: 6 }}>
      <Angabe beschriftung="Angebotsnummer">{angebot.angebotsnummer}</Angabe>
      <Angabe beschriftung="Status">
        <Statusmerkmal status={angebot.status} />
      </Angabe>
      <Angabe beschriftung="Interessent">{angebot.interessentName}</Angabe>
      <Angabe beschriftung="Jahresverbrauch">{zahl(angebot.jahresverbrauchKwh)} kWh</Angabe>
      <Angabe beschriftung="Arbeitspreis">
        {zahl(angebot.arbeitspreisCtBrutto, 4)} ct/kWh brutto
      </Angabe>
      <Angabe beschriftung="Grundpreis">{geld(angebot.grundpreisBrutto)} je Jahr</Angabe>
      <Angabe beschriftung="Jahresbetrag">
        <strong>{geld(angebot.jahresbetragBrutto)}</strong>
      </Angabe>
      <Angabe beschriftung="Bindungsfrist bis">{datum(angebot.bindungsfristBis)}</Angabe>
      <Angabe beschriftung="Abschlusskanal">{angebot.abschlusskanal}</Angabe>
    </div>
  )
}
